import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Dropdown from 'react-bootstrap/Dropdown'

import Loading from '../components/psc/lib/Loading'

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useBrowserTabTitle from '../hooks/useBrowserTabTitle';

const ProjectDashboard = () => {
    const pscState = useSelector(state => state.pscState.value)
    const project = pscState.project
    const isLoading = pscState.isLoading

    useBrowserTabTitle(`${project?.project_code || ''} Dashboard`.trim())
    
    const { currentUser } = useAuthentication();

    const {
        userHasProjectAccess,
        userHasProjectAccessLevel,
    } = useUserManagement()

    // For responsiveness
    const columnDef="col-md-4 col-lg-3 col-xl-2"

    const [statistics, setStatistics] = useState([])
    useEffect(() => {
        if (!project) {
            return
        }
        fetch('/v0/projects/' + project.id + '/statistics', { credentials: 'same-origin' })
            .then(response => response.json())
            .then(json => setStatistics(json.result.statistics))
    }, [project])
    
    const renderStatistics = () => {
        if (!project || !statistics || !statistics.map || statistics.length === 0) {
            return null
        }
        
        const stats = statistics.map(stat => {
            // find project_type item with same name as this statistics item
            // in order to use the href value in the link
            const linkObj = project.project_type.items.find(item => item.name === stat.name) || { href: '' }
            
            const linkLocation = linkObj.href.replace('$project_id', project.id)
            const plusLinkLocation = linkObj.href.replace('$project_id', project.id) + '/new'
            const canAdd = linkObj.href.includes('/included_documents') ? isCadmController : isPlusUser
            const stonlyClass = 'stonly-dashboard-' + linkObj.item_name
            
            // 4 possible scenarios
            // 1 - No project access. No linking
            // 2 - Read-only access
            // 4 - Write access to /reports, dropdown Add icon
            // 3 - Write access, simple Add icon

            if ( ! userHasProjectAccess(currentUser, project.id) ) {
                return (
                    <div className={columnDef} key={stat.name}>
                        <div className={`widget p-lg text-center stats ${stonlyClass}`}>
                            <div className="icon-link m-b-md">
                                <i className={`fa ${stat.fa_icon} fa-5x`}></i>
                                <h1 className="m-xs">{stat.count}</h1>
                                <p> {stat.name} </p>
                            </div>
                        </div>
                    </div>
                )
            }
            if ( ! canAdd ) {
                return (
                    <div className={columnDef} key={stat.name}>
                        <div className={`widget p-lg text-center stats stats-link ${stonlyClass}`}>
                            <Link to={`${linkLocation}`} className="icon-link m-b-md" data-tooltip-id="gnctooltip" data-tooltip-content={`${stat.tooltip_goto}`}>
                                <i className={`fa ${stat.fa_icon} fa-5x`}></i>
                                <h1 className="m-xs">{stat.count}</h1>
                                <p> {stat.name} </p>
                            </Link>
                        </div>
                    </div>
                )
            }
            if (linkObj.href.includes('/reports')) {
                const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
                    <a
                        href=""
                        className="icon-plus-link"
                        data-tooltip-id="gnctooltip" data-tooltip-content={`${stat.tooltip_create_new}`}
                        ref={ref}
                        onClick={(e) => {
                                e.preventDefault();
                                onClick(e);
                            }}
                    >
                        {children}
                    </a>
                ));
                const CustomMenu = React.forwardRef(
                    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
                        style.left = "-150px"
                        return (
                            <div
                                ref={ref}
                                style={style}
                                className={className}
                                aria-labelledby={labeledBy}
                            >
                                <ul className="m-t-xs dashboard-plus-menu">
                                    {React.Children.toArray(children)}
                                </ul>
                            </div>
                        );
                    },
                );
                CustomToggle.displayName = 'CustomToggle'
                CustomMenu.displayName = 'CustomMenu'
                
                return (
                    <div className={columnDef} key={stat.name}>
                        <div className={`widget p-lg text-center stats stats-link ${stonlyClass}`}>
                            <Dropdown style={{position: 'inherit'}}>
                                <Dropdown.Toggle as={CustomToggle}>
                                    <i className="fa fa-plus-circle"></i>
                                </Dropdown.Toggle>

                                <Dropdown.Menu as={CustomMenu}>
                                    <Dropdown.Item><Link className="dropdown-item" to={`${plusLinkLocation}`}>New Status Report</Link></Dropdown.Item>
                                    <Dropdown.Item><Link className="dropdown-item" to={`${plusLinkLocation}?has_stages=1`}>New Tracking Report</Link></Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                            
                            <Link to={`${linkLocation}`} className="icon-link m-b-md" data-tooltip-id="gnctooltip" data-tooltip-content={`${stat.tooltip_goto}`}>
                                <i className={`fa ${stat.fa_icon} fa-5x`}></i>
                                <h1 className="m-xs">{stat.count}</h1>
                                <p> {stat.name} </p>
                            </Link>
                        </div>
                    </div>
                )
            }
            
            return (
                <div className={columnDef} key={stat.name}>
                    <div className={`widget p-lg text-center stats stats-link ${stonlyClass}`}>
                        <Link to={`${plusLinkLocation}`} className="icon-plus-link" data-tooltip-id="gnctooltip" data-tooltip-content={`${stat.tooltip_create_new}`}>
                            <i className="fa fa-plus-circle"></i>
                        </Link>
                        <Link to={`${linkLocation}`} className="icon-link m-b-md" data-tooltip-id="gnctooltip" data-tooltip-content={`${stat.tooltip_goto}`}>
                            <i className={`fa ${stat.fa_icon} fa-5x`}></i>
                            <h1 className="m-xs">{stat.count}</h1>
                            <p> {stat.name} </p>
                        </Link>
                    </div>
                </div>
            )
        })
        
        const infoLink = '/portfolio/' + project.id + '/information'
        const stonlyClass = 'stonly-dashboard-information'
        return (
            <div className="ibox-content" style={{minHeight: '400px'}}>
                <div className="row">
                    <div className={columnDef} key="information">
                        <div className={`widget p-lg text-center stats stats-link ${stonlyClass}`}>
                            <Link to={infoLink} className="icon-link m-b-md" data-tooltip-id="gnctooltip" data-tooltip-content="Go to Information">
                                <i className={'fa fa-info-circle fa-5x'}></i>
                                <h1 className="m-xs"> &nbsp; </h1>
                                <p> Information </p>
                            </Link>
                        </div>
                    </div>
                    
                    {stats}
                </div>
            </div>
        )
    }
    
    if( ! project ) {
        return <h1>Loading...</h1>
    }

    const isUser              = userHasProjectAccess(currentUser, project.id);
    const isPlusUser          = userHasProjectAccessLevel(currentUser, project.id, 'MEMBER');
    const isCadmController    = userHasProjectAccessLevel(currentUser, project.id, 'CADM_CONTROLLER');
    
    const type = project && project.project_type
        ? project.project_type.name.toLowerCase()
        : 'project'

    if( isLoading ) {
        return <Loading />
    }
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Dashboard</h2>

                            {(!isUser && !isPlusUser) && 
                             <div key="newText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                                 You are <strong>not currently a member</strong> of this {type}.<br/>
                             </div>
                            }
                            {(isUser && !isPlusUser) && 
                                 <div key="newText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                                     Your {type} membership is: <strong>View all areas (Read-only)</strong><br/>
                                     Select any area below to view full tables and contents. <br/>
                                     You can return to Dashboard at any time by clicking the {type} name above.</div>
                            }
                            {(isPlusUser && !isCadmController) &&
                                 <div key="newText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                                     Your {type} membership is: <strong>Full Member</strong><br/>
                                     Quick-add <i className="fa fa-plus-circle"></i> a new item, or click through to full tables and options in each area. <br/>
                                     You can return to Dashboard at any time by clicking the {type} name above. </div>
                            }
                            {isCadmController && 
                                 <div key="newText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                                     Your {type} membership is: <strong>CADM Controller</strong><br/>
                                     Quick-add <i className="fa fa-plus-circle"></i> a new item, or click through to full tables and options in each area. <br/>
                                     You can return to Dashboard at any time by clicking the {type} name above. </div>
                            }
            
                        </div>
                        {renderStatistics()}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProjectDashboard
